import React, { useEffect, useState } from 'react';


const displayValue = value => value === null || value === undefined || value === '' ? '—' : String(value);
const numericValue = value => value === null || value === undefined || value === '' ? NaN : Number(value);

export const formatDuration = value => {
  const seconds = numericValue(value);
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const rounded = Math.round(seconds);
  const hours = Math.floor(rounded / 3600);
  const minutes = Math.floor((rounded % 3600) / 60);
  return [hours ? `${hours}h` : '', minutes ? `${minutes}m` : '', `${rounded % 60}s`].filter(Boolean).join(' ');
};

export const formatTimestamp = row => {
  const raw = row.timestamp > 0 ? row.timestamp : row.occurred_at || (row.timestamp === 0 && !('occurred_at' in row) ? 0 : '');
  if (raw === null || raw === undefined || raw === '') return '—';
  // Epoch values are seconds; backend date strings are UTC, including legacy naive strings.
  const numeric = numericValue(raw);
  const date = Number.isFinite(numeric)
    ? new Date(numeric * 1000)
    : new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw) ? raw : `${String(raw).replace(/ UTC$/, '').replace(' ', 'T')}Z`);
  return Number.isNaN(date.getTime()) ? '—' : date.toISOString().replace('T', ' ').replace('.000Z', ' UTC').replace('Z', ' UTC');
};

export const fetchDashboardSection = async (section, options = {}) => {
  const response = await fetch(`/api/sirri_api/womensinfo/${section}`, options);
  if (!response.ok) throw new Error(`${section}: HTTP ${response.status}`);
  if (!(response.headers.get('content-type') || '').toLowerCase().includes('application/json')) {
    throw new Error(`${section}: expected JSON (the admin session may have expired)`);
  }
  let data;
  try {
    data = await response.json();
  } catch (error) {
    throw new Error(`${section}: invalid JSON response`);
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error(`${section}: invalid response`);
  if (data.error || data.success === false) throw new Error(`${section}: ${data.error || data.message || 'request failed'}`);
  return data;
};

export const TestimonialLink = ({ row }) => {
  const url = row.testimonial_url || row.video_url;
  return typeof url === 'string' && /^https?:\/\//i.test(url)
    ? <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline break-all">{url}</a>
    : <span className="text-gray-500">Specific testimonial link not recorded</span>;
};

export const DashboardSection = ({ title, children, open = false, ...props }) => (
  <details className="bg-white text-gray-900 rounded-lg shadow mb-6" open={open} {...props}>
    <summary className="px-6 py-4 border-b cursor-pointer text-lg font-semibold">{title}</summary>
    {children}
  </details>
);

export const TestimonialPlaybackTotals = ({ data }) => {
  if (!Array.isArray(data.playback_totals)) {
    return <p className="px-6 py-4 text-sm text-gray-500">Playback totals not provided by the server.</p>;
  }
  const totals = [...data.playback_totals].sort((a, b) => b.play_count - a.play_count || String(a.testimonial_url).localeCompare(String(b.testimonial_url)));
  return (
    <div className="px-6 py-4 border-b">
      <h4 className="font-semibold">Recorded plays by testimonial</h4>
      <p>Overall recorded plays: {displayValue(data.total_recorded_plays)}</p>
      <p>Unattributed recorded plays: {displayValue(data.unattributed_play_count)}</p>
      <p className="text-sm text-gray-600">{data.playback_note || 'Recorded start beacons, not verified completed watches or unique viewers. Totals cover all matching retained events, not just displayed raw rows. Repeated recorded starts count again; replays without a new beacon do not.'}</p>
      {totals.length ? <table className="min-w-full divide-y divide-gray-200">
        <thead><tr><th scope="col" className="text-left">Testimonial link</th><th scope="col" className="text-left">Recorded plays</th></tr></thead>
        <tbody>{totals.map((item, index) => <tr key={item.testimonial_url || index}>
          <td className="py-2"><TestimonialLink row={item} /></td><td>{displayValue(item.play_count)}</td>
        </tr>)}</tbody>
      </table> : <p className="text-sm text-gray-500">No recorded playback starts with a specific testimonial URL.</p>}
    </div>
  );
};

export const ActivityCard = ({ title, data, duration = false, messaging = false, video = false, controls = null, hideContent = false }) => {
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const count = Number.isInteger(data.count) && data.count >= 0 ? data.count : rows.length;
  const columns = ['Occurred at (UTC)', messaging ? 'Actor / sender email' : 'Viewer email', 'Viewed / recipient email', 'Viewed name', 'Event', 'Status'];
  if (duration) columns.push('Recorded duration');
  if (video) columns.push('Testimonial link');
  return (
    <DashboardSection title={title}>
      {controls}
      {!hideContent && <>
      <div className="px-6 py-4 border-b">
        <p className="text-sm text-gray-500">Recorded events: {count}</p>
        {duration && <p className="text-sm text-gray-500">Total recorded duration: {formatDuration(data.total_duration_seconds)}</p>}
        <p className="text-sm text-gray-500">Source: {data.source || 'Not provided by the server'}</p>
        {messaging && <p className="text-sm text-gray-500">Recorded messaging activity is not proof of recipient delivery or reading.</p>}
        {data.note && <p className="text-sm text-gray-600 mt-2">{data.note}</p>}
      </div>
      {video && <TestimonialPlaybackTotals data={data} />}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>{columns.map(column => <th key={column} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{column}</th>)}</tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.length ? rows.map((row, index) => (
              <tr key={index}>
                <td className="px-6 py-4 text-sm whitespace-nowrap">{formatTimestamp(row)}{row.timestamp_kind === 'scheduled_for' && <span className="block text-xs text-gray-500">Scheduled for</span>}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.viewer_email || row.actor || row.visitor_id)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.viewed_email)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.viewed_name)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.event_name)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.status)}</td>
                {duration && <td className="px-6 py-4 text-sm whitespace-nowrap">{formatDuration(row.duration_seconds)}</td>}
                {video && <td className="px-6 py-4 text-sm"><TestimonialLink row={row} /></td>}
              </tr>
            )) : <tr><td colSpan={columns.length} className="px-6 py-4 text-sm text-gray-500 text-center">No records returned for this activity. This does not establish that no activity occurred.</td></tr>}
          </tbody>
        </table>
      </div>
      {count > rows.length && <p className="px-6 py-3 text-sm text-gray-500">Showing {rows.length} of {count} recorded events.</p>}
      </>}
    </DashboardSection>
  );
};

export function TestimonialViewsPanel({ initialData }) {
  // Keep this filter independent of assessment and the other dashboard sections.
  const [period, setPeriod] = useState('all');
  const [result, setResult] = useState({period: 'all', data: initialData});
  const [error, setError] = useState('');
  const [requestId, setRequestId] = useState(0);
  useEffect(() => {
    if (requestId === 0) return undefined;
    const controller = new AbortController();
    fetchDashboardSection(`testimonial_views${period === '336' ? '?hours=336' : ''}`, {
      signal: controller.signal, credentials: 'same-origin', cache: 'no-store',
    }).then(data => {
      if (!controller.signal.aborted) setResult({period, data});
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.message || 'Unable to load testimonials.');
    });
    return () => controller.abort();
  }, [period, requestId]);
  const select = value => {
    setPeriod(value); setResult(null); setError(''); setRequestId(requestId + 1);
  };
  const current = result?.period === period ? result.data : null;
  const controls = <div className="px-6 py-4 border-b">
    <div role="group" aria-label="Testimonial period" className="flex gap-2">
      {[['all', 'All history'], ['336', 'Last 2 weeks']].map(([value, label]) =>
        <button key={value} type="button" aria-pressed={period === value}
          className={`border rounded px-4 py-2 ${period === value ? 'bg-blue-700 text-white' : ''}`}
          onClick={() => select(value)}>{label}</button>)}
    </div>
    <p>Selected period: {period === '336' ? 'Last 2 weeks (rolling 336 hours)' : 'All history (all retained events)'}. Applies to recorded events and playback totals only in this section.</p>
    {error ? <p role="alert">{error} <button type="button" onClick={() => select(period)}>Retry</button></p>
      : !current && <p role="status">Loading testimonials…</p>}
  </div>;
  return <ActivityCard title="Testimonial Views" data={current || {}} video controls={controls} hideContent={!current} />;
}

export const ProfileDurationCard = ({ data }) => {
  const rows = Array.isArray(data.rows) ? data.rows : [];
  const count = Number.isInteger(data.count) ? data.count : rows.length;
  const columns = ['Recipient email', 'Recipient name', 'Total viewing time (last 24 hours)', 'Distinct viewers', 'Duration records', 'Last recorded at (UTC)'];
  return (
    <DashboardSection title="Time Spent Viewing Profiles — Last 24 Hours">
      <div className="px-6 py-4 border-b">
        <p className="text-sm text-gray-500">Recipient profiles: {count}</p>
        <p className="text-sm text-gray-500">Total recorded duration: {formatDuration(data.total_duration_seconds)}</p>
        <p className="text-sm text-gray-500">Duration records: {displayValue(data.event_count)}</p>
        {data.unattributed_event_count > 0 && <p className="text-sm text-gray-600">Excluded records without recipient email: {data.unattributed_event_count}</p>}
        <p className="text-sm text-gray-500">Source: {data.source || 'Not provided by the server'}</p>
        {data.note && <p className="text-sm text-gray-600 mt-2">{data.note}</p>}
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50"><tr>{columns.map(column => <th key={column} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{column}</th>)}</tr></thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {rows.length ? rows.map((row, index) => (
              <tr key={row.viewed_email || index}>
                <td className="px-6 py-4 text-sm">{displayValue(row.viewed_email)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.viewed_name)}</td>
                <td className="px-6 py-4 text-sm whitespace-nowrap">{formatDuration(row.duration_seconds)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.viewer_count)}</td>
                <td className="px-6 py-4 text-sm">{displayValue(row.event_count)}</td>
                <td className="px-6 py-4 text-sm whitespace-nowrap">{formatTimestamp(row)}</td>
              </tr>
            )) : <tr><td colSpan={columns.length} className="px-6 py-4 text-sm text-gray-500 text-center">No recorded profile durations with a recipient email in the last 24 hours.</td></tr>}
          </tbody>
        </table>
      </div>
      {count > rows.length && <p className="px-6 py-3 text-sm text-gray-500">Showing {rows.length} of {count} recipient profiles. Totals cover all recipients.</p>}
    </DashboardSection>
  );
};

export const eatInput = epoch => new Date(epoch + 3 * 3600000).toISOString().slice(0, 16);

export function validateAssessmentInterval(start, end) {
  const parse = value => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return NaN;
    const epoch = Date.parse(`${value}:00+03:00`);
    return Number.isFinite(epoch) && eatInput(epoch) === value ? epoch : NaN;
  };
  const first = parse(start), last = parse(end);
  return !Number.isFinite(first) || !Number.isFinite(last)
    ? 'Enter valid start and end dates/times in EAT.'
    : first >= last ? 'Start must be before end; end is exclusive.' : '';
}

export async function fetchAssessmentInterval(filters, signal, section = 'assessment_events') {
  const response = await fetch(`/api/sirri_api/womensinfo/${section}?${new URLSearchParams(filters)}`, {
    credentials: 'same-origin', cache: 'no-store', signal,
  });
  if (!response.ok) throw new Error(response.status === 403 ? 'Admin session required. Please sign in again.' : `Assessment events: HTTP ${response.status}`);
  if (!(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Assessment events: expected JSON.');
  const data = await response.json();
  if (data?.responsecode !== 200 || !Array.isArray(data.rows)) throw new Error(data?.error || 'Invalid assessment event response.');
  return data;
}

export const assessmentHeadlineStages = [
  ['Landing page', 'contact_viewed'],
  ['Filled Biodata', 'contact_submission_succeeded'],
  ['gender_viewed', 'gender_viewed'],
  ['Age Question', 'age_viewed'],
  ['Location Question', 'location_viewed'],
  ['Relationship Intention Question', 'relationship_intention_viewed'],
  ['Meeting Preference Question', 'meeting_preference_viewed'],
  ['Financial Readiness Question', 'financial_readiness_viewed'],
  ['When to Begin Question', 'begin_timing_viewed'],
  ['Qualified', 'qualified_submission_succeeded'],
  ['Disqualified', 'disqualified_submission_succeeded'],
  ['Started pitch video', 'video_started'],
  ['Finished pitch video', 'video_completed'],
  ['Clicked Continue to Site', 'continue_clicked'],
  ['Clicked Bachelors', 'bachelors_clicked'],
  ['Clicked Testimonials', 'testimonials_clicked'],
];

export function assessmentHeadlineCount(data, suffix) {
  const counts = data.unique_visitors_by_event;
  if (!data.storage_available || !counts || typeof counts !== 'object') return 'Unknown';
  const name = `assessment_private_${suffix}`;
  const count = counts[name];
  if (!(name in counts)) return data.counts && !(name in data.counts) ? 0 : 'Unknown';
  return Number.isInteger(count) && count >= 0 ? count : 'Unknown';
}

export function formatAssessmentBound(value, includeYear = true) {
  if (!value) return 'Unknown interval';
  // API bounds, not editable controls; explicit offsets and naive EAT wall clocks work alike.
  const date = new Date(/(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}:00+03:00`);
  if (!Number.isFinite(date.getTime())) return 'Unknown interval';
  const day = new Intl.DateTimeFormat('en-GB', {timeZone: 'Africa/Nairobi', day: 'numeric', month: 'long', ...(includeYear ? {year: 'numeric'} : {})}).format(date);
  const time = new Intl.DateTimeFormat('en-US', {timeZone: 'Africa/Nairobi', hour: 'numeric', minute: '2-digit', hour12: true}).format(date).toLowerCase().replace(':00', '');
  return `${day}, ${time}`;
}

export function existingPackageText(data) {
  const heading = '**Existing women by current package — last 14 days login breakdown**';
  if (!data?.storage_available || !Array.isArray(data.packages)) return `${heading}\nUnknown — existing-women package data unavailable or incomplete.`;

  const count = value => Number.isInteger(value) && value >= 0 ? value : 'Unknown';
  return [heading,
    `Snapshot: ${formatAssessmentBound(data.generated_at)} EAT.`,
    `Login window: ${formatAssessmentBound(data.login_window_start)} → ${formatAssessmentBound(data.login_window_end)} EAT (inclusive).`,
    'All existing women; current package, not package at login. Independent of assessment date/domain filters.',
    `Existing women: ${data.existing_count}; logged in during last 14 days: ${data.recent_count}. Distinct women, not login events.`,
    'Country is the recorded current profile country, not nationality or login location.',
    ...data.packages.flatMap(row => [
      `${row.package}: ${row.existing_count} existing, ${row.recent_count} logged in in last 14 days`,
      ...['Tanzania', 'Kenya', 'Other', 'Unknown'].filter(country =>
        country === 'Tanzania' || country === 'Kenya' ||
        row.country_breakdown?.[country]?.existing_count > 0 ||
        row.country_breakdown?.[country]?.recent_count > 0
      ).map(country => {
        const counts = row.country_breakdown?.[country];
        return `- ${country}: ${count(counts?.existing_count)} existing, ${count(counts?.recent_count)} logged in in last 14 days`;
      }),
    ]),
    !data.packages.length ? 'No existing women found.' : '',
  ].filter(Boolean).join('\n');
}

export function assessmentHeadlineText(data) {
  return [`**${formatAssessmentBound(data.start, false)} → ${formatAssessmentBound(data.end)} EAT**`,
    '**Entry and questions**',
    ...assessmentHeadlineStages.map(([label, suffix]) => `- ${label}:${assessmentHeadlineCount(data, suffix)}`),
    '', existingPackageText(data.existing_user_packages),
  ].join('\n');
}

export function AssessmentHeadline({ data }) {
  const [copyStatus, setCopyStatus] = useState('');
  return <section aria-label="Assessment headline report" className="p-4 border rounded mb-4">
    <p><strong>{formatAssessmentBound(data.start, false)} → {formatAssessmentBound(data.end)} EAT</strong></p>
    <p><strong>Entry and questions</strong></p>
    <ul>{assessmentHeadlineStages.map(([label, suffix]) => <li key={suffix}>{label}:{assessmentHeadlineCount(data, suffix)}</li>)}</ul>
    <p>Domain: {data.host || 'All domains'}. Unique recorded visitors per event, across sessions; not raw events. Missing visitor IDs are excluded. End exclusive.</p>
    <pre className="whitespace-pre-wrap break-words mt-4">{existingPackageText(data.existing_user_packages)}</pre>
    <button type="button" className="border rounded px-4 py-2" onClick={async () => {
      try {
        await navigator.clipboard.writeText(`${assessmentHeadlineText(data)}\n\nDomain: ${data.host || 'All domains'}. Unique recorded visitors per event; missing visitor IDs excluded. End exclusive.`);
        setCopyStatus('Report copied.');
      } catch (error) { setCopyStatus('Could not copy report. Select and copy the report above.'); }
    }}>Copy report</button>
    <p role="status">{copyStatus}</p>
  </section>;
}

export function AssessmentIntervalReport({ data }) {
  if (!data) return null;
  if (!data.storage_available) return <><AssessmentHeadline key={JSON.stringify(data)} data={data} />{ <p role="status">Assessment bridge storage is unavailable for all or part of this interval. Counts are unknown, not zero. Check the configured bridge storage/mount. {(data.warnings || []).join(', ')}</p>}</>;
  const names = Object.keys(data.counts || {}).sort();
  return <div>
    <AssessmentHeadline key={JSON.stringify([data.start, data.end, data.host, data.unique_visitors_by_event])} data={data} />
    <details open><summary className="cursor-pointer font-semibold">Raw assessment event detail</summary>
    <p>Applied interval (EAT / Africa/Nairobi): {data.start} ≤ server receipt time &lt; {data.end}. Domain: {data.host || 'All domains'}.</p>
    <p>Distinct recorded visitors: {data.unique_visitors} · Raw events: {data.total_events} · Visitor/event pairs: {data.unique_visitor_event_pairs} · Events missing visitor ID: {data.missing_visitor_events}</p>
    <p>Unique visitors per event across all sessions (visitor_id, falling back to cookie_visitor_id). Missing IDs are excluded from uniques and counted separately. Visitors can occur in multiple events; do not sum stages as people or completed assessments. Counts cover the full interval before recent-row limiting.</p>
    {(data.warnings || []).map(warning => <p key={warning}>{warning}</p>)}
    {!names.length ? <p>No matching assessment_ events recorded in the readable interval.</p> : <div className="overflow-x-auto"><table className="min-w-full text-left">
      <thead><tr>{['Event', 'Unique visitors (across sessions)', 'Raw events', 'Missing visitor ID events'].map(label => <th key={label} className="p-2">{label}</th>)}</tr></thead>
      <tbody>{names.map(name => <tr key={name}><td className="p-2 break-all">{name}</td><td className="p-2">{data.unique_visitors_by_event?.[name] ?? '—'}</td><td className="p-2">{data.counts[name]}</td><td className="p-2">{data.missing_visitors_by_event?.[name] || 0}</td></tr>)}</tbody>
    </table></div>}
    <p>Recent rows: {data.rows.length} of {data.total_events}{data.has_more ? ' (limited; totals above include all matching events)' : ''}.</p>
    <ul>{data.rows.map((row, index) => <li key={index}>{eatInput(row.server_received_epoch * 1000).replace('T', ' ')} EAT — {row.event_name}</li>)}</ul>
    </details>
  </div>;
}

export function SavedLeadValues({ value }) {
  // Render every saved key, including unknown future answers and tracking keys.
  // Text/JSON only: even malformed/javascript tracking URLs never become links.
  return <pre className="whitespace-pre-wrap break-all">{value === undefined ? 'Not recorded' : JSON.stringify(value, null, 2)}</pre>;
}

export function AssessmentLeadsReport({ data }) {
  if (!data) return null;
  return <>
    <p>Applied interval (EAT): {data.start} ≤ latest saved time &lt; {data.end}. Domain: {data.host || 'All domains'}.</p>
    <p>Latest saved snapshot per lead across storage, then interval/domain filters, then row limit. This is saved lead data, not a count of all telemetry visitors. Contact details are admin-only. Historical fields not recorded cannot be recovered.</p>
    {!data.storage_available ? <p role="status">Saved lead storage is unavailable or incomplete. Lead totals are unknown, not zero. {(data.warnings || []).join(', ')}</p> : <>
      <p>Showing {data.rows.length} of {data.count} saved leads{data.has_more ? ' (limited)' : ''}.</p>
      {!data.rows.length && <p>No saved leads match these filters in readable storage.</p>}
      {data.rows.map((row, index) => <details key={row.lead_id || index} className="border rounded p-3 my-2">
        <summary className="cursor-pointer">{displayValue(row.lead_id)} — {displayValue(row.status)} — {displayValue(row.saved_at)}</summary>
        <p>Status: {displayValue(row.status)} · Schema version: {row.schema_version ?? row.tracking?.schema_version ?? 'Not recorded'}</p>
        <p>Disqualification reason: {!('disqualification_reason' in row) ? 'Not recorded' : row.disqualification_reason === null ? 'None' : displayValue(row.disqualification_reason)}</p>
        <h4>Contact</h4><SavedLeadValues value={row.contact} />
        <h4>All saved assessment answers</h4><SavedLeadValues value={row.answers} />
        <h4>All saved tracking</h4><SavedLeadValues value={row.tracking} />
      </details>)}
    </>}
  </>;
}

export function AssessmentLeadsPanel({ filters }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setData(null); setError('');
    if (!filters) { setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true);
    fetchAssessmentInterval(filters, controller.signal, 'assessment_leads').then(result => {
      if (!controller.signal.aborted) setData(result);
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.message || 'Unable to load saved leads.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters]);
  return <details className="border rounded p-4 mt-4">
    <summary className="cursor-pointer font-semibold">Saved assessment leads — full answers and tracking (admin only)</summary>
    {!filters && <p>Apply the date/time and domain filters above to load saved leads.</p>}
    {error && <p role="alert">Saved leads: {error}</p>}
    {loading ? <p role="status">Loading saved leads…</p> : !error && <AssessmentLeadsReport data={data} />}
  </details>;
}

export function AssessmentIntervalPanel() {
  const [start, setStart] = useState(() => eatInput(Date.now() - 86400000));
  const [end, setEnd] = useState(() => eatInput(Date.now()));
  const [host, setHost] = useState('');
  const [filters, setFilters] = useState(null);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!filters) { setLoading(false); return undefined; }
    const controller = new AbortController();
    setLoading(true); setError(''); setData(null);
    Promise.all([
      fetchAssessmentInterval(filters, controller.signal),
      fetch('/api/sirri_api/womensinfo/existing_user_packages', {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal,
      }).then(async response => {
        if (!response.ok || !(response.headers.get('content-type') || '').includes('application/json')) throw new Error('Package report unavailable');
        const report = await response.json();
        if (report?.responsecode !== 200 || !Array.isArray(report.packages)) throw new Error('Invalid package report');
        return report;
      }).catch(() => ({storage_available: false})),
    ]).then(([result, packages]) => {
      if (!controller.signal.aborted) setData({...result, existing_user_packages: packages});
    }).catch(err => {
      if (!controller.signal.aborted) setError(err.message || 'Unable to load assessment events.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [filters]);
  return <DashboardSection title="Assessment events — date and time" open aria-label="Assessment event date and time filters">
    <div className="p-6">
    <p>All assessment_ prefix events. EAT / Africa/Nairobi (UTC+03:00), independent of your browser timezone. Start inclusive, end exclusive; cross-day intervals supported.</p>
    <form className="flex flex-wrap gap-3 items-end my-3" onSubmit={event => {
      event.preventDefault();
      const invalid = validateAssessmentInterval(start, end);
      setError(invalid);
      if (invalid) { setData(null); setFilters(null); return; }
      setFilters({start, end, host, limit: '100'});
    }}>
      <label>Start (EAT / Africa/Nairobi)<input className="block border p-2" type="datetime-local" required value={start} onChange={event => setStart(event.target.value)} /></label>
      <label>End exclusive (EAT / Africa/Nairobi)<input className="block border p-2" type="datetime-local" required value={end} onChange={event => setEnd(event.target.value)} /></label>
      <label>Domain<select className="block border p-2" value={host} onChange={event => setHost(event.target.value)}><option value="">All domains</option>{['penzi-halisi.com', 'upendo-halisi.com', 'expatelitesingles.com'].map(value => <option key={value} value={value}>{value}</option>)}</select></label>
      <button className="border rounded px-4 py-2" type="submit" disabled={loading}>{loading ? 'Loading…' : 'Filter assessment events / Refresh'}</button>
    </form>
    {error && <p role="alert">{error}</p>}
    {loading ? <p role="status">Loading assessment events…</p> : !error && <AssessmentIntervalReport data={data} />}
    <AssessmentLeadsPanel filters={filters} />
    </div>
  </DashboardSection>;
}

const WomenInfoMetrics = () => {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          userLogins,
          testimonialViews,
          profileViews,
          messagingAttempts,
          messagesSent,
          timeSpent,
          recentLogins,
          packageCounts,
        ] = await Promise.all([
          fetchDashboardSection('user_logins'),
          fetchDashboardSection('testimonial_views'),
          fetchDashboardSection('profile_views'),
          fetchDashboardSection('messaging_attempts'),
          fetchDashboardSection('messages_sent'),
          fetchDashboardSection('time_spent_viewing_profiles'),
          fetchDashboardSection('recent_logins_last_14d'),
          fetchDashboardSection('package_login_counts_last_14d'),
        ]);

        setSections({
          userLogins,
          testimonialViews,
          profileViews,
          messagingAttempts,
          messagesSent,
          timeSpent,
          recentLogins,
          packageCounts,
        });
        setLoading(false);
      } catch (e) {
        setError(e.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600 mt-8">Error loading dashboard: {error}</p>;
  }

  return (
    <div className="p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6">Womensinfo Dashboard</h2>

      {/* Section 1: User Logins */}
      {sections.userLogins && sections.userLogins.rows && (
        <DashboardSection title="User Logins Log">
          <div className="px-6 py-4 border-b">
            <p className="text-sm text-gray-500">Total login events: {sections.userLogins.count}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login At (UTC)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sections.userLogins.rows.slice(0, 20).map((row, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4 text-sm font-medium">{row.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{row.email}</td>
                    <td className="px-6 py-4 text-sm">{row.login_at}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{row.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DashboardSection>
      )}

      {sections.testimonialViews && <TestimonialViewsPanel initialData={sections.testimonialViews} />}
      {sections.profileViews && <ActivityCard title="Profile Views" data={sections.profileViews} duration />}
      {sections.messagingAttempts && <ActivityCard title="Messaging Attempts" data={sections.messagingAttempts} messaging />}
      {sections.messagesSent && <ActivityCard title="Messages Sent" data={sections.messagesSent} messaging />}
      {sections.timeSpent && <ProfileDurationCard data={sections.timeSpent} />}

      {/* Section 7: Recent 14-Day Logins */}
      {sections.recentLogins && (
        <DashboardSection title="Recent Logins (Last 14 Days)">
          <div className="px-6 py-4 border-b">
            <p className="text-sm text-gray-500">
              Processed: {sections.recentLogins.processed}, Recent logins: {sections.recentLogins.recent_logins}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logins</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sections.recentLogins.people && sections.recentLogins.people.length > 0 ? (
                  sections.recentLogins.people.slice(0, 10).map((p, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4 text-sm font-medium">{p.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{p.email}</td>
                      <td className="px-6 py-4 text-sm">{p.package}</td>
                      <td className="px-6 py-4 text-sm">{p.login_count}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{formatTimestamp({ timestamp: p.last_login })}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-sm text-gray-500 text-center">No people logged in during this reporting window.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DashboardSection>
      )}

      {/* Section 8: Package Login Counts (Last 14 Days) */}
      {sections.packageCounts && (
        <DashboardSection title="Package Login Counts (Last 14 Days)">
          <div className="px-6 py-4 border-b">
            <p className="text-sm text-gray-500">Distinct users per package who logged in during the last 14 days.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Women</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sections.packageCounts.packages && sections.packageCounts.packages.length > 0 ? (
                  sections.packageCounts.packages.map((pkg, i) => (
                    <tr key={i}>
                      <td className="px-6 py-4 text-sm font-medium">{pkg.package}</td>
                      <td className="px-6 py-4 text-sm font-medium">{pkg.count}</td>
                      <td className="px-6 py-4 text-sm text-gray-500">{pkg.women}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-6 py-4 text-sm text-gray-500 text-center">No logins found in this reporting window.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DashboardSection>
      )}
    </div>
  );
};

export default function WomenInfoDashboard() {
  return <><AssessmentIntervalPanel /><WomenInfoMetrics /></>;
}